/**
 * Address / cadastre cache matching helpers (QA-018).
 * Postcode-only matches are intentionally rejected.
 * House numbers must match unless the typed number is missing or equals the NPA
 * (postcode typed as housenumber, e.g. "Chemin Du Criblet 1315 La Sarraz").
 *
 * Matching is exact-string OR street + house + postcode — no prefix shortcut
 * (prefixes on long street names falsely equated different housenumbers).
 */

/** Prefer the last Swiss NPA in the string (housenumber may also be 4 digits). */
function extractPostcode(s) {
  const matches = [...String(s).matchAll(/\b([1-9]\d{3})\b/g)];
  if (!matches.length) return '';
  return matches[matches.length - 1][1];
}

/**
 * Parse street name + optional housenumber before the Swiss postcode.
 * If the typed housenumber equals the postcode, treat it as NPA-as-house (ignored).
 */
export function parseStreetAndHouse(s) {
  const raw = String(s || '').trim().toLowerCase();
  const postcode = extractPostcode(raw);
  let before = raw;
  if (postcode) {
    const idx = raw.lastIndexOf(postcode);
    before = (idx >= 0 ? raw.slice(0, idx) : raw).replace(/[,\s]+$/g, '').trim();
  }
  const hm = before.match(/^(.*?)(?:\s+|,\s*)(\d+[a-z]?)$/i);
  if (!hm) {
    return { street: before, house: null, postcode, npaAsHouse: false };
  }
  const street = hm[1].replace(/[,\s]+$/g, '').trim();
  const house = hm[2].toLowerCase();
  if (postcode && house === postcode) {
    return { street, house: null, postcode, npaAsHouse: true };
  }
  return { street, house, postcode, npaAsHouse: false };
}

function streetsCompatible(a, b) {
  if (!a || !b || a.length < 8 || b.length < 8) return false;
  return a === b || a.includes(b) || b.includes(a);
}

export function cacheMatchesQuery(query, resolvedLabel) {
  if (!query || query.length < 5 || !resolvedLabel) return false;
  const q = query.trim().toLowerCase();
  const c = String(resolvedLabel).trim().toLowerCase();
  if (q === c) return true;

  const pq = parseStreetAndHouse(q);
  const pc = parseStreetAndHouse(c);
  if (!pq.postcode || pq.postcode !== pc.postcode) return false;
  if (!streetsCompatible(pq.street, pc.street)) return false;

  // Real typed housenumber must match the resolved housenumber.
  if (pq.house) {
    return Boolean(pc.house) && pq.house === pc.house;
  }
  // Query has no housenumber, or NPA was typed as housenumber — street + postcode enough.
  return true;
}
